import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Target, TrendingUp, AlertCircle, Search } from 'lucide-react';

export default function MarginGuideSidebar({ 
  services = [], 
  processors = [], 
  isOpen, 
  onToggle,
  selectedService = null 
}) {
  const [targetMargin, setTargetMargin] = useState(55);
  const [calculationMatrix, setCalculationMatrix] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('55');
  const [searchTerm, setSearchTerm] = useState('');
  const [enableACH, setEnableACH] = useState(false); // Toggle for ACH providers
  const PRESET_TARGETS = [
    { value: 55, label: '55%', color: 'bg-green-600', description: 'Primary Target' },
    { value: 48, label: '48%', color: 'bg-blue-600', description: 'Secondary Target' }
  ];

  const calculateRequiredTakeRate = (merchantFeePercent, targetMarginPercent) => {
    if (targetMarginPercent >= 100 || targetMarginPercent < 0) return null;
    const targetMarginDecimal = targetMarginPercent / 100;
    const merchantFeeDecimal = merchantFeePercent / 100;
    const requiredServiceRate = merchantFeeDecimal / (1 - targetMarginDecimal);
    return requiredServiceRate * 100;
  };

  // Build calculation matrix - grouped by service, one processor section per processor
  useEffect(() => {
    if (services.length === 0 || processors.length === 0) return;

    const matrix = services.map(service => {
      const serviceData = {
        serviceName: service.name || 'Unknown',
        serviceId: service.id,
        processorGroups: []
      };

      processors.forEach(processor => {
        if (!processor.name || processor.name === 'N/A') return;
        
        const serviceFees = processor.service_fees?.[service.id];
        
        // Determine processor type based on name
        const isACHProcessor = processor.name.toLowerCase().includes('ach');
        const isCardProcessor = !isACHProcessor;
        
        // Select appropriate payment types based on processor type
        const paymentTypes = isACHProcessor 
          ? ['ACH']  // ACH processors only show ACH
          : ['CC', 'AMEX', 'DEBIT'];  // Card processors show card types only
        
        const paymentRates = [];
        
        paymentTypes.forEach(paymentType => {
          let merchantFeePercent = 0;
          
          if (serviceFees?.[paymentType] !== undefined) {
            merchantFeePercent = serviceFees[paymentType] * 100;
          } else if (processor.fees?.[paymentType] !== undefined) {
            merchantFeePercent = processor.fees[paymentType] * 100;
          }
          
          if (!merchantFeePercent) return;
          
          const requiredRate = calculateRequiredTakeRate(merchantFeePercent, targetMargin);
          if (!requiredRate || isNaN(requiredRate)) return;
          
          paymentRates.push({
            type: paymentType,
            merchantFeePercent,
            requiredRate
          });
        });
        
        if (paymentRates.length > 0) {
          serviceData.processorGroups.push({
            processorName: processor.name,
            paymentRates
          });
        }
      });

      return serviceData;
    });

    setCalculationMatrix(matrix.filter(s => s.processorGroups.length > 0));
  }, [services, processors, targetMargin, enableACH]);

  // Filter by search term and ACH toggle
  const filteredMatrix = calculationMatrix
    .map(service => {
      if (!enableACH) {
        // Filter out ACH processor groups when ACH is disabled
        return {
          ...service,
          processorGroups: service.processorGroups.filter(group => 
            !group.processorName.toLowerCase().includes('ach')
          )
        };
      }
      return service;
    })
    .filter(service => 
      service.serviceName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      service.processorGroups.length > 0 // Only show services with visible processors
    );

  const formatRate = (rate) => {
    if (!rate || isNaN(rate)) return 'N/A';
    if (rate > 100) return '>100%';
    return `${rate.toFixed(2)}%`;
  };

  const getPaymentBadgeColor = (type) => {
    const colors = {
      'ACH': 'bg-green-100 text-green-700',
      'CC': 'bg-blue-100 text-blue-700',
      'AMEX': 'bg-orange-100 text-orange-700',
      'DEBIT': 'bg-purple-100 text-purple-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getRateColor = (rate) => {
    if (rate > 10) return 'text-red-600';
    if (rate > 5) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <>
      <button
        onClick={onToggle}
        className={`fixed top-1/2 -translate-y-1/2 bg-indigo-600 text-white p-3 shadow-lg hover:bg-indigo-700 transition-all duration-300 z-50 ${
          isOpen ? 'right-[500px] rounded-l-lg' : 'right-0 rounded-l-lg'
        }`}
        title={isOpen ? 'Close Margin Guide' : 'Open Smart Margin Guide'}
      >
        {isOpen ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
      </button>

      <div className={`fixed right-0 top-0 h-full bg-white shadow-2xl border-l border-slate-200 transition-all duration-300 z-40 ${
        isOpen ? 'w-[500px]' : 'w-0'
      } overflow-hidden`}>
        
        {isOpen && (
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-6 h-6 text-indigo-600" />
                <h2 className="text-xl font-bold text-slate-900">Smart Margin Guide</h2>
              </div>
              <p className="text-sm text-slate-600">Calculate required service rates to hit your target margin</p>
            </div>

            {/* Target Margin Controls */}
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Target Profit Margin</label>
              
              <div className="flex gap-2 mb-3">
                {PRESET_TARGETS.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => {
                      setTargetMargin(preset.value);
                      setSelectedPreset(preset.value.toString());
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedPreset === preset.value.toString()
                        ? `${preset.color} text-white shadow-md`
                        : 'bg-white text-slate-700 border border-slate-300 hover:border-indigo-400'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={targetMargin}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setTargetMargin(Math.min(99, Math.max(0, val)));
                    setSelectedPreset('custom');
                  }}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <span className="text-sm font-medium text-slate-600">% Custom</span>
              </div>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-slate-200 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              {/* ACH Toggle */}
              <label className="flex items-center gap-2 cursor-pointer p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={enableACH}
                  onChange={(e) => setEnableACH(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-slate-700">Enable ACH Providers</span>
              </label>
              
              {searchTerm && (
                <p className="text-xs text-slate-600 mt-1">
                  Found {filteredMatrix.length} service{filteredMatrix.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Matrix Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Required Take Rates by Service</h3>
              
              {filteredMatrix.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>{searchTerm ? 'No services match your search' : 'No rate data available'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMatrix.map((serviceData) => (
                    <div key={serviceData.serviceId} className="border border-slate-200 rounded-lg overflow-hidden">
                      {/* Service Header */}
                      <div className="bg-indigo-50 px-3 py-2 border-b border-indigo-100">
                        <h4 className="font-semibold text-slate-800">{serviceData.serviceName}</h4>
                      </div>

                      {/* Processor Groups */}
                      {serviceData.processorGroups.map((processorGroup, idx) => (
                        <div key={idx} className="p-3 border-b border-slate-100 last:border-b-0">
                          <div className="font-medium text-sm text-slate-700 mb-2">{processorGroup.processorName}</div>
                          
                          <div className="space-y-2">
                            {processorGroup.paymentRates.map((rate, rIdx) => (
                              <div key={rIdx} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded font-medium ${getPaymentBadgeColor(rate.type)}`}>
                                    {rate.type}
                                  </span>
                                  <span className="text-slate-600">
                                    MF: {rate.type === 'ACH' && rate.merchantFeePercent === 0 
                                      ? 'N/A (flat fee)' 
                                      : `${rate.merchantFeePercent.toFixed(2)}%`}
                                  </span>
                                </div>
                                <span className={`font-bold ${getRateColor(rate.requiredRate)}`}>
                                  {formatRate(rate.requiredRate)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Legend */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <p className="text-xs font-semibold text-slate-700 mb-2">Rate Color Guide:</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-600"></div>
                  <span className="text-slate-700">&lt;5% (Low)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-600"></div>
                  <span className="text-slate-700">5-10% (Moderate)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-600"></div>
                  <span className="text-slate-700">&gt;10% (High)</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
